/**
 * Standard R.N. Elliott degree notation: colour, line style, and how a wave label is written.
 *
 * <h2>Seven tiers here, four from the engine</h2>
 *
 * The full ladder is defined because that is the notation chartists read, and because a caller
 * plotting counts from any source should be able to ask for "Supercycle" and get the right pen.
 *
 * The engine, however, does not emit Elliott's tier names, and deliberately so — from its own
 * WaveDegree docs:
 *
 *   "Elliott's own degree names are absolute claims about where a move sits in a decades-long
 *    hierarchy, and nothing observable in a 2,000-bar 5-minute window can establish that."
 *
 * It emits MICRO / MINOR / INTERMEDIATE / MACRO, which are *relative*: finest to coarsest among
 * the resolutions it actually ran. {@link ENGINE_DEGREE_TIERS} maps those four onto four tiers of
 * the ladder so they draw with distinct, conventional pens.
 *
 * That map is a <b>presentation choice, not a claim</b>. A count the engine calls MACRO draws in
 * Primary green because it is the coarsest structure found in this window — not because the engine
 * has established it is a Primary-degree wave. Preserving the relative ordering is what matters;
 * the absolute tier is borrowed for its colour.
 */

/** Roman numerals for the wave positions Elliott notation ever needs (five per motive pattern). */
const ROMAN = ['I', 'II', 'III', 'IV', 'V'];

/**
 * The ladder, coarsest first.
 *
 * `numerals` decides how a motive wave's number is written; `wrap` brackets it; `lowercase`
 * applies to corrective letters as well as roman numerals, which is what distinguishes Minute's
 * `i, ii, a, b` from Cycle's `I, II, A, B`.
 */
export const DEGREE_STYLES = {
  GRAND_SUPERCYCLE: {
    name: 'Grand Supercycle',
    color: '#9C27B0',
    lineStyle: 'solid',
    width: 3,
    numerals: 'roman',
    wrap: ['[', ']'],
    lowercase: false,
  },
  SUPERCYCLE: {
    name: 'Supercycle',
    color: '#2196F3',
    lineStyle: 'solid',
    width: 2.5,
    numerals: 'roman',
    wrap: ['(', ')'],
    lowercase: false,
  },
  CYCLE: {
    name: 'Cycle',
    color: '#00BCD4',
    lineStyle: 'solid',
    width: 2,
    numerals: 'roman',
    wrap: ['', ''],
    lowercase: false,
  },
  PRIMARY: {
    name: 'Primary',
    color: '#4CAF50',
    lineStyle: 'solid',
    width: 2,
    numerals: 'arabic',
    wrap: ['[', ']'],
    lowercase: false,
  },
  INTERMEDIATE: {
    name: 'Intermediate',
    color: '#FF9800',
    lineStyle: 'solid',
    width: 1.5,
    numerals: 'arabic',
    wrap: ['(', ')'],
    lowercase: false,
  },
  MINOR: {
    name: 'Minor',
    color: '#E91E63',
    lineStyle: 'dashed',
    width: 1.5,
    numerals: 'arabic',
    wrap: ['', ''],
    lowercase: false,
  },
  MINUTE: {
    name: 'Minute',
    color: '#9E9E9E',
    lineStyle: 'dotted',
    width: 1,
    numerals: 'roman',
    wrap: ['', ''],
    lowercase: true,
  },
};

/**
 * The engine's four relative degrees, mapped onto four tiers of the ladder.
 *
 * Ordering is preserved — MICRO is the finest pen and MACRO the boldest — so a chart carrying
 * counts at several degrees reads correctly whatever the absolute tiers really are. MINOR and
 * INTERMEDIATE happen to keep their own names; MICRO and MACRO borrow the tiers either side.
 */
export const ENGINE_DEGREE_TIERS = {
  MICRO: 'MINUTE',
  MINOR: 'MINOR',
  INTERMEDIATE: 'INTERMEDIATE',
  MACRO: 'PRIMARY',
};

/** Fallback pen: never throws, never draws nothing, and is visibly unstyled if a name is unknown. */
const FALLBACK = DEGREE_STYLES.MINOR;

/**
 * The style for a degree name.
 *
 * Accepts either an engine degree (MICRO, MACRO…) or a ladder tier (PRIMARY, SUPERCYCLE…), so a
 * caller does not have to know which vocabulary its data speaks.
 */
export function styleForDegree(degree) {
  if (!degree) return FALLBACK;
  const key = String(degree).trim().toUpperCase().replace(/[\s-]+/g, '_');
  const tier = ENGINE_DEGREE_TIERS[key] || key;
  return DEGREE_STYLES[tier] || FALLBACK;
}

/**
 * A wave label written in the notation of its degree.
 *
 * The engine labels waves `1`–`5` for motive patterns and `A`/`B`/`C` (or `W`/`X`/`Y`) for
 * corrective ones. Which numerals and brackets those are written with is what identifies the
 * degree on a chart, so the transformation belongs here rather than at each call site.
 *
 *   formatWaveLabel('3', 'INTERMEDIATE')  ->  '(3)'
 *   formatWaveLabel('3', 'MINOR')         ->  '3'
 *   formatWaveLabel('3', 'MICRO')         ->  'iii'
 *   formatWaveLabel('B', 'MACRO')         ->  '[B]'
 *   formatWaveLabel('B', 'MICRO')         ->  'b'
 */
export function formatWaveLabel(label, degree) {
  if (label === null || label === undefined || label === '') return '';
  const style = styleForDegree(degree);
  const raw = String(label).trim();

  let text = raw;
  const asNumber = Number(raw);
  if (Number.isInteger(asNumber) && asNumber >= 1 && asNumber <= ROMAN.length) {
    // Only motive wave numbers convert to numerals. A corrective label is already a letter.
    text = style.numerals === 'roman' ? ROMAN[asNumber - 1] : raw;
  }
  if (style.lowercase) text = text.toLowerCase();

  return `${style.wrap[0]}${text}${style.wrap[1]}`;
}

/** ECharts `lineStyle.type` for a degree. The vocabularies happen to coincide; the map is explicit. */
export function lineTypeForDegree(degree) {
  const style = styleForDegree(degree);
  switch (style.lineStyle) {
    case 'dashed': return 'dashed';
    case 'dotted': return 'dotted';
    default: return 'solid';
  }
}
